import { Theme } from "@mui/material";
import dayjs from "@/lib/dayjs";

export const formatCountryCode = (code?: string): string => {
  if (!code) return "+1";
  return code.startsWith("+") ? code : `+${code}`;
};

export const formatDate = (dateString: string | number | Date) => {
  if (!dateString) return "-";
  return dayjs(dateString).tz().format("DD MMM, YYYY");
};

export const formatDateTime = (dateString: string | number | Date) => {
  if (!dateString) return "-";
  return dayjs(dateString).tz().format("DD MMM, YYYY, hh:mm A");
};

export const toChicagoUTC = (date?: string | number | Date | dayjs.Dayjs, mode?: "start" | "end") => {
  let d = dayjs.tz(date);
  if (mode === "start") d = d.startOf("day");
  if (mode === "end") d = d.endOf("day");
  return d.utc().format();
};

export const formatAddress = (addr?: string | { houseNo?: string, area?: string, landmark?: string, city?: string, state?: string, pinCode?: string | number, country?: string }) => {
  if (!addr) return "-";
  if (typeof addr === "string") return addr;
  const { houseNo, area, landmark, city, state, pinCode, country } = addr;
  return [houseNo, area, landmark, city, state, pinCode, country].filter(Boolean).join(", ") || "-";
};

export const formatFileSize = (bytes: number) => {
  if (!bytes) return "0 KB";

  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;

  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;

  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
};


export const DESCRIPTION_MAX_LENGTH = 500;

export const publicPaths = ["/login", "/signup", "/design-guide", "/session-expired", "/forgot-password", "/change-password", "/otp-verify", "/policy", "/delete_account_request"];
export const Regex = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/,
  password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
  decimal: /^\d*\.?\d{0,2}$/,
  integer: /^\d*$/,
  onlyNumber: /^[0-9]$/,
};


export const renderList = (list?: (string | { name: string })[]) =>
  Array.isArray(list) && list.length > 0
    ? list.map(item => typeof item === "string" ? item : item.name).join(", ")
    : "-";

export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  path: string;
  role?: string[];
  expanded?: boolean;
  badge?: number;
  subMenu?: MenuItem[];
}

export const convertFieldNameToLabel = (fieldName: string): string => {
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};


export const appliesToOptions = [
  "All Orders",
  "Order Total",
  // "Box",
  "New Users",
  "Subscribed Users",
  "One-Time Orders",
  "Flash Sale Discount",
  "Other",
];

export const discountTypes = [
  "FLAT",
  "PERCENTAGE",
  // "Cart Value Discount",
  // "First Order Discount",
  // "Premium Member Discount",
  // "Flash Sale Discount",
  // "Other"
];

const COUPON_CODE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

export const generateCouponCode = (length = 8): string => {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => COUPON_CODE_CHARS[byte % COUPON_CODE_CHARS.length]).join("");
};

export const userGroupOptions = [
  { name: "New Users", _id: "NEW" },
  { name: "Existing Users", _id: "EXISTING" },
  // { name: "Premium Users", _id: "PREMIUM" },
];


export const menuItems: MenuItem[] = [
  // {
  //   "id": "dashboard",
  //   "label": "Dashboard",
  //   "icon": "Dashboard.svg",
  //   "path": "/dashboard",
  //   "role": ["ADMIN", "KITCHEN"],
  // },
  {
    "id": "orders",
    "label": "Orders",
    "icon": "Orders.svg",
    "path": "/orders",
    "role": ["ADMIN", "KITCHEN", "AGENCY"]
  },
  {
    "id": "menu",
    "label": "Menu",
    "icon": "Menu.svg",
    "path": "/menu",
    "expanded": true,
    "role": ["ADMIN", "KITCHEN"],
    "subMenu": [
      {
        "id": "categories",
        "label": "Categories",
        "path": "/menu/categories",
        "role": ["ADMIN"],
      },
      {
        "id": "dishes",
        "label": "Dishes",
        "path": "/menu/dishes",
        "role": ["ADMIN", "KITCHEN"],
      },
      {
        "id": "sides",
        "label": "Sides",
        "path": "/menu/sides",
        "role": ["ADMIN", "KITCHEN"],
      },
      {
        "id": "drinks",
        "label": "Drinks",
        "path": "/menu/drinks",
        "role": ["ADMIN", "KITCHEN"],
      },
      {
        "id": "schedule_menu",
        "label": "Schedule Menu",
        "path": "/menu/schedule-menu",
        "role": ["ADMIN", "KITCHEN"],
      }
    ]
  },
  {
    "id": "customers",
    "label": "Customers",
    "icon": "Customers.svg",
    "path": "/customers",
    "role": ["ADMIN"]
  },
  {
    "id": "preference_dietary",
    "label": "Preference & Allergy",
    "icon": "Preference_And_Dietary.svg",
    "path": "/preference-dietary",
    "role": ["ADMIN"]
  },
  {
    "id": "kitchens",
    "label": "Kitchens",
    "icon": "Kitchens.svg",
    "path": "/kitchens",
    "badge": 6,
    "role": ["ADMIN"]
  },
  {
    "id": "Delivery",
    "label": "Delivery",
    "icon": "Menu.svg",
    "path": "/delivery",
    "role": ["ADMIN", "AGENCY"],
    "expanded": true,
    "subMenu": [
      {
        "id": "Agencies",
        "label": "Agencies",
        "path": "/delivery/agencies",
        "role": ["ADMIN"]
      },
      {
        "id": "drivers",
        "label": "Drivers",
        "path": "/delivery/drivers",
        "role": ["AGENCY", "ADMIN"]
      },
    ]
  },
  {
    "id": "discount_codes",
    "label": "Discount Codes",
    "icon": "Discount_Codes.svg",
    "path": "/discount-codes",
    "role": ["ADMIN"]
  },
  {
    "id": "offers",
    "label": "Offers",
    "icon": "Offers.svg",
    "path": "/offers",
    "role": ["ADMIN"]
  },
  {
    "id": "settings",
    "label": "Settings",
    "icon": "Settings.svg",
    "path": "/settings",
    "expanded": true,
    "role": ["ADMIN"],
    "subMenu": [
      // {
      //   "id": "general",
      //   "label": "General",
      //   "path": "/settings/general",
      //   "role": ["ADMIN"]
      // },
      {
        "id": "policies",
        "label": "Policies",
        "path": "/settings/policies",
        "role": ["ADMIN"]
      },
      {
        "id": "mail_templates",
        "label": "Mail Templates",
        "path": "/settings/mail-templates",
        "role": ["ADMIN"]
      },
      {
        "id": "notification_templates",
        "label": "Notification Templates",
        "path": "/settings/notification-templates",
        "role": ["ADMIN"]
      },
      {
        "id": "imp_links",
        "label": "Document Links",
        "path": "/settings/imp-links",
        "role": ["ADMIN"]
      },
      {
        "id": "app_config",
        "label": "App Config",
        "path": "/settings/app-config",
        "role": ["ADMIN"]
      },
      // {
      //   "id": "users_roles",
      //   "label": "Users & Roles",
      //   "path": "/settings/users-roles",
      //   "role": ["ADMIN"]
      // }
    ]
  },
  {
    "id": "supports",
    "label": "Supports",
    "icon": "Supports.svg",
    "path": "/supports",
    "role": ["ADMIN"]
  },
  // {
  //   "id": "help",
  //   "label": "Help",
  //   "icon": "Help.svg",
  //   "path": "/help",
  //   "role": ["ADMIN"]
  // }
];

export const getFilteredMenuItems = (userRole: string): MenuItem[] => {
  const hasAccess = (roles?: string[]) => !roles || roles.includes(userRole);
  return menuItems.map(item => {
    if (!hasAccess(item.role)) return null;
    const subMenu = item.subMenu?.filter(sub => hasAccess(sub.role));
    if (item.subMenu && !subMenu?.length) return null;
    return { ...item, subMenu };
  }).filter(Boolean) as MenuItem[];
};

export const getFirstMenuPath = (userRole: string): string => {
  const filtered = getFilteredMenuItems(userRole);
  if (filtered.length > 0) {
    if (filtered[0].subMenu && filtered[0].subMenu.length > 0) {
      return filtered[0].subMenu[0].path;
    }
    return filtered[0].path;
  }
  return "/login";
};

export const isPathAllowed = (path: string, userRole: string): boolean => {
  const purePath = path.split("?")[0];
  if (publicPaths.some(p => purePath.startsWith(p))) return true;
  if (purePath === "/" || purePath === "/access-denied") return true;

  const findPath = (items: MenuItem[]): MenuItem | undefined => {
    for (const item of items) {
      if (item.subMenu) {
        const sub = findPath(item.subMenu);
        if (sub) return sub;
      }
      if (purePath === item.path || purePath.startsWith(item.path + "/")) return item;
    }
  };

  const menuItem = findPath(menuItems);
  if (!menuItem) return true;

  return !menuItem.role || menuItem.role.includes(userRole);
};



export const portionSizeUnitOptions = [
  { label: "Piece (pcs)", value: "pcs" },
  { label: "Plate (plt)", value: "plt" },
  { label: "Bowl (bowl)", value: "bowl" },
  { label: "Ounce (oz)", value: "oz" },
  { label: "Fluid Ounce (fl oz)", value: "fl_oz" },
];

export const portionTypeOptions = [
  // { label: "Regular", value: "Regular" },
  // { label: "Small", value: "Small" },
  // { label: "Medium", value: "Medium" },
  // { label: "Large", value: "Large" },
  { label: "Classic", value: "Classic" },
  // { label: "Deluxe", value: "Deluxe" },
  { label: "Feast", value: "Feast" },
];

export interface ImageObject {
  url: string;
  actualFileName: string;
  isCover: boolean;
  type?: "Image" | "Video";
}

export const normalizeImages = (images?: (string | Partial<ImageObject>)[]): ImageObject[] => {
  if (!images || !Array.isArray(images)) return [];
  return images.map((img) => {
    if (typeof img === "string") {
      const isVideo = img.toLowerCase().endsWith(".mp4") || img.toLowerCase().endsWith(".webm");
      return {
        url: img,
        actualFileName: img.split("/").pop() || "",
        isCover: false,
        type: isVideo ? "Video" : "Image",
      };
    }
    const isVideo = img.url?.toLowerCase().endsWith(".mp4") || img.url?.toLowerCase().endsWith(".webm") || img.type === "Video";
    return {
      url: img.url || "",
      actualFileName: img.actualFileName || img.url?.split("/").pop() || "",
      isCover: !!img.isCover,
      type: isVideo ? "Video" : "Image",
    };
  });
};

export const redirectScreenOptions = [
  { label: "Wallet Page", value: "WALLET" },
  { label: "Card Page", value: "CARD" },
];
export const redirectAppOptions = [
  { label: "Mealy App", value: "MEALY" },
  { label: "The Food Exchange Web", value: "TFE" },
];

export const nutritionFields = [
  { label: "Calories", field: "calories", unit: "kcal" },
  { label: "Protein", field: "protein", unit: "oz" },
  { label: "Carbs", field: "carbs", unit: "oz" },
  { label: "Fats", field: "fats", unit: "oz" },
  { label: "Fiber", field: "fiber", unit: "oz" },
];

export const getCalendarHeaderLabel = (
  activeTab: number,
  currentDate: Date,
  calendarDays: { date: Date; key: string; isCurrentMonth: boolean }[]
): string => {
  const short = (d: Date) => d.toLocaleDateString("en-US", { month: "short" });
  const long = (d: Date) => d.toLocaleDateString("en-US", { month: "long" });

  if (activeTab === 0) {
    return currentDate.toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  }

  if (calendarDays.length === 0) return "";
  const first = calendarDays[0].date;
  const last = calendarDays[calendarDays.length - 1].date;

  const sameYear = first.getFullYear() === last.getFullYear();
  const sameMonth = sameYear && first.getMonth() === last.getMonth();

  if (sameMonth) {
    return `${long(first)} ${first.getDate()} \u2013 ${last.getDate()}, ${first.getFullYear()}`;
  }
  if (sameYear) {
    return `${short(first)} ${first.getDate()} \u2013 ${short(last)} ${last.getDate()}, ${first.getFullYear()}`;
  }
  return `${short(first)} ${first.getDate()}, ${first.getFullYear()} \u2013 ${short(last)} ${last.getDate()}, ${last.getFullYear()}`;
};


const colorMap = [
  { color: "custom.lightGray", statuses: ["YET_TO_ORDER"] },
  { color: "primary.main", statuses: ["CREATED", "FIRST_ORDER", "PICKUP"] },
  { color: "info.main", statuses: ["IN_TRANSIT"] },
  { color: "success.dark", statuses: ["READY_TO_DELIVER"] },
  { color: "success.main", statuses: ["ACTIVE", "APPROVED", "CLOSED", "DELIVERED", "DELIVERY", "ONLINE", "RESOLVED", "YES"] },
  { color: "warning.light", statuses: ["PREPARING", "WAITING"] },
  { color: "warning.main", statuses: ["CONFIRMED", "PENDING", "SCHEDULED", "UPCOMING", "NO"] },
  { color: "error.dark", statuses: ["REJECTED"] },
  { color: "error.main", statuses: ["CANCELLED", "EXPIRED", "INACTIVE", "OFFLINE", "OPEN"] },
];

export const ORDER_STATUS_COLOR: Record<string, string> = Object.fromEntries(
  colorMap.flatMap(({ color, statuses }) => statuses.map(s => [s, color]))
);

export const capitalizeFirstLetter = (str: string | boolean): string => {
  if (typeof str === "boolean") {
    str = str ? "ACTIVE" : "INACTIVE";
  }
  if (!str) return "-";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase().replace(/_/g, " ");
};

export const getStatusColor = (status: string | boolean, theme: Theme): string => {
  if (typeof status === "boolean") {
    status = status ? "ACTIVE" : "INACTIVE";
  }
  const colorPath = ORDER_STATUS_COLOR[status] || "text.secondary";
  const parts = colorPath.split(".");
  let color: unknown = theme.palette;
  for (const part of parts) {
    color = (color as Record<string, unknown>)?.[part];
  }
  return typeof color === "string" ? color : colorPath;
};
